import { useTranslation } from "react-i18next";
import { Box } from "@material-ui/core";
import { OptionGroup } from "components/Styled";
import SwitchField from "components/Forms/SwitchField";
import { emailFlowFilter } from "pages/Rules/Form/context/ruleSelectors";
import { RuleType } from "pages/Rules/common/types";
import FilterFromTo from "./FilterFromTo";
import ExcludeMatchingSelector from "./ExcludeMatchingSelector";
import FilterPolicyToggle from "../FilterPolicyToggle";
import { useRuleContext } from "../../context/ruleContext";

import { EmailFlowFilter, ExcludeMatching, FromTo } from "@dashboard-v3/api";

export default function EmailFlowOptions() {
  const { t } = useTranslation("rules");
  const { state, dispatch } = useRuleContext();
  const { actionType } = state.rule;
  const { errors } = state.filters.targetFilter;
  let filter = emailFlowFilter(state.rule);

  function updateFilter(updates: Partial<EmailFlowFilter>) {
    filter = { ...filter, ...updates };
    if (filter.triggerOnShortCode) {
      filter.cancelShortCodeEnabled = false;
    }
    dispatch({ type: "updateFilterTarget", payload: filter });
  }

  function updateExclusions(exclude: FromTo[]) {
    // Without a list to match against, the option means nothing to the engine.
    const excludeMatching = exclude?.length
      ? filter?.excludeMatching
      : undefined;
    updateFilter({ exclude, excludeMatching });
  }

  return (
    <>
      <OptionGroup>
        <FilterPolicyToggle filterType="TARGET" />
      </OptionGroup>
      {showShortCodeOption(state.type) && (
        <>
          <OptionGroup>
            <SwitchField
              name="triggerOnShortCode"
              label={t("triggerOnShortCode")}
              description={t(`triggerOnShortCodeDescription.${actionType}`)}
              value={filter?.triggerOnShortCode}
              onChange={event => {
                updateFilter({ triggerOnShortCode: event.target.checked });
              }}
            />
          </OptionGroup>
          <OptionGroup>
            <SwitchField
              name="cancelShortCodeEnabled"
              label={t("cancelShortCodeEnabled")}
              description={t("cancelShortCodeEnabledDescription")}
              value={filter?.cancelShortCodeEnabled}
              disabled={!!filter?.triggerOnShortCode}
              onChange={event => {
                updateFilter({ cancelShortCodeEnabled: event.target.checked });
              }}
            />
          </OptionGroup>
        </>
      )}
      {showOnlyManagedAccounts(state.type) && (
        <OptionGroup>
          <SwitchField
            name="onlyManagedAccounts"
            label={t("onlyManagedAccounts")}
            description={t("onlyManagedAccountsDescription")}
            value={filter?.onlyManagedAccounts}
            onChange={e =>
              updateFilter({ onlyManagedAccounts: e.target.checked })
            }
          />
        </OptionGroup>
      )}

      <OptionGroup>
        <FilterFromTo
          type="exclude"
          value={filter?.exclude}
          errors={errors.exclusions}
          onChange={updateExclusions}
        />
        {Boolean(filter?.exclude?.length) && (
          <Box mt={3} ml={2}>
            <ExcludeMatchingSelector
              value={filter?.excludeMatching}
              onChange={(excludeMatching: ExcludeMatching) =>
                updateFilter({ excludeMatching })
              }
            />
          </Box>
        )}
      </OptionGroup>
    </>
  );
}

function showShortCodeOption(type: RuleType) {
  return type === "REPLACE_ATTACHMENTS" || type === "PROTECT_EMAILS";
}

function showOnlyManagedAccounts(type: RuleType) {
  return [
    "COPY_EMAILS",
    "PROTECT_EMAILS",
    "REPLACE_ATTACHMENTS",
    "BLOCK_ATTACHMENTS",
    "SEND_DELAY",
  ].some(t => t === type);
}
